import getConfig from "next/config";
import {
  ArticleResponse,
  CategoryResponse,
  CmsListing,
  DevelopmentResponse,
  GuideCategoryResponse,
  GuideResponse,
  ListingResponse,
  NeighbourhoodResponse,
  TagResponse,
} from "./apiTypes";
import Strapi, { StrapiRequestParams } from "strapi-sdk-js";
const { publicRuntimeConfig } = getConfig();
export function getCMSURL(path = "") {
  return `${publicRuntimeConfig.NEXT_PUBLIC_CMS_API_URL}${path}`;
}

const strapi = () => {
  return new Strapi({
    prefix: "/api",
    url: getCMSURL(),
    axiosOptions: {
      // Strapi 5 compatibility: keep receiving the v4 response shape
      headers: { "Strapi-Response-Format": "v4" },
    },
  });
};

export const getListings = (params: StrapiRequestParams) => {
  return strapi()
    .find("listings", params)
    .then((res) => res as ListingResponse);
};

export const getArticles = (params: StrapiRequestParams) => {
  return strapi()
    .find("articles", params)
    .then((res) => res as ArticleResponse);
};

export const getCategories = () => {
  return strapi()
    .find("categories")
    .then((res) => res as CategoryResponse);
};

export const getGuideCategories = () => {
  return strapi()
    .find("guide-categories")
    .then((res) => res as GuideCategoryResponse);
};

export const getGuides = (params: StrapiRequestParams) => {
  return strapi()
    .find("guides", params)
    .then((res) => res as GuideResponse);
};

export const getTags = () => {
  return strapi()
    .find("tags")
    .then((res) => res as TagResponse);
};

export const getNeighbourhood = (slug: string, populate: string[] = []) => {
  return strapi()
    .find("neighbourhoods", {
      filters: {
        slug: {
          $eq: slug,
        },
      },
      populate,
    })
    .then((res) => res as NeighbourhoodResponse);
};

export const getOneDevelopment = async () => {
  return strapi()
    .find("developments", {
      populate: ["neighbourhood", "cover"],
      pagination: { page: 1, pageSize: 1 },
      sort: "publishedAt:desc",
      filters: {
        isUpcomingProject: true,
      },
    })
    .then((res) => {
      const { data } = res as DevelopmentResponse;
      if (data.length > 0) {
        return data[0];
      }
      return undefined;
    });
};

export const getSingleDevelopment = async (neighbourhood: string) => {
  return strapi()
    .find("developments", {
      populate: ["neighbourhood", "cover"],
      pagination: { page: 1, pageSize: 1 },
      sort: "publishedAt:desc",
      filters: {
        neighbourhood: {
          slug: {
            $eq: neighbourhood,
          },
        },
      },
    })
    .then((res) => {
      const { data } = res as DevelopmentResponse;
      if (data.length > 0) {
        return data[0];
      }
      return undefined;
    });
};

export const getFutureDevelopments = async (neighbourhoods: string[]) => {
  return strapi()
    .find("developments", {
      populate: ["neighbourhood", "cover"],
      pagination: { page: 1, pageSize: 10 },
      sort: "publishedAt:desc",
      filters: {
        neighbourhood: {
          slug: {
            $in: neighbourhoods,
          },
        },
        isUpcomingProject: true,
      },
    })
    .then((res) => {
      const { data } = res as DevelopmentResponse;
      if (data) {
        return data;
      }
      return undefined;
    });
};

// v5 rejects pagination nested in a populate fragment, so page listings as a separate top-level query.
// MAX_LISTING_PAGES hard-caps iterations regardless of what the API reports for pageCount.
const MAX_LISTING_PAGES = 1000;

async function fetchListingsByNeighbourhoodSlugs(
  slugs: string[],
  fields: string[],
): Promise<CmsListing[]> {
  if (slugs.length === 0) return [];
  const pageSize = 100;
  let page = 1;
  let pageCount = 1;
  const all: CmsListing[] = [];

  do {
    const res = (await strapi().find("listings", {
      filters: { neighbourhoods: { slug: { $in: slugs } } },
      fields,
      populate: { neighbourhoods: { fields: ["slug"] } },
      pagination: { page, pageSize },
    })) as ListingResponse;
    const items = res.data ?? [];
    if (items.length === 0) break;
    all.push(...items);
    pageCount = Math.min(res.meta?.pagination?.pageCount ?? 1, MAX_LISTING_PAGES);
    page += 1;
  } while (page <= pageCount);

  return all;
}

export const getHomepageDevelopments = async () => {
  const res = await strapi().find("developments", {
    populate: {
      neighbourhood: { fields: ["slug"] },
      cover: true,
    },
    pagination: { page: 1, pageSize: 10 },
    sort: "homepageOrder:asc",
    filters: {
      showOnHomepage: true,
    },
  });
  const { data } = res as DevelopmentResponse;
  if (!data) return [];

  const slugs = data
    .map((development) => development.attributes.neighbourhood?.data?.attributes.slug)
    .filter((slug): slug is string => Boolean(slug));
  // comingSoon is a ribbonType value, not a real field — v5 rejects it in `fields`.
  const listings = await fetchListingsByNeighbourhoodSlugs(slugs, ["ribbonType"]);

  return data.map((development) => {
    const slug = development.attributes.neighbourhood?.data?.attributes.slug;
    const neighbourhoodListings = listings.filter((listing) =>
      listing.attributes.neighbourhoods.data?.some((n) => n.attributes.slug === slug),
    );
    if (development.attributes.neighbourhood?.data) {
      development.attributes.neighbourhood.data.attributes.listings = {
        data: neighbourhoodListings,
      };
    }
    return development;
  });
};

export const getDevelopementsDropdowns = async () => {
  const res = await strapi().find("developments", {
    populate: {
      neighbourhood: { fields: ["slug"] },
    },
    pagination: { page: 1, pageSize: 100 },
    sort: "publishedAt:desc",
  });
  const { data } = res as DevelopmentResponse;
  if (!data) return undefined;

  const slugs = data
    .map((development) => development.attributes.neighbourhood?.data?.attributes.slug)
    .filter((slug): slug is string => Boolean(slug));
  const listings = await fetchListingsByNeighbourhoodSlugs(slugs, ["ribbonType"]);

  return data
    .filter((development) => development.attributes.dropdownHeading !== null)
    .map((development) => {
      const slug = development.attributes.neighbourhood?.data?.attributes.slug;
      const neighbourhoodListings = listings.filter((listing) =>
        listing.attributes.neighbourhoods.data?.some((n) => n.attributes.slug === slug),
      );
      const availableCount = neighbourhoodListings.filter(
        (listing) =>
          listing.attributes.ribbonType !== "reserved" &&
          listing.attributes.ribbonType !== "let" &&
          listing.attributes.ribbonType !== "comingSoon",
      ).length;

      return {
        key: slug ?? "",
        heading: development.attributes.dropdownHeading,
        subHeading: development.attributes.dropdownSubheading,
        pillText: availableCount === 0 ? "Sold out" : undefined,
      };
    })
    .sort((a, b) => {
      if (!!a.pillText !== !!b.pillText) {
        return a.pillText ? 1 : -1;
      }
      return a.heading.localeCompare(b.heading);
    });
};
